import { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, MenuItem } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import PageContainer, {
  PageHeader,
  PageTitle,
  PageTitleWrapper,
} from 'components/PageContainer';
import {
  URL_MY_SCORECARDS,
  URL_PERSONAL_DEVELOPMENT_PLAN,
} from 'constants/urls';
import { scorecardsTexts, pdpTexts, commonTexts } from 'i18n';
import CreateScorecardDialog, {
  createScorecardDialogMessages,
} from 'components/CreateScorecardDialog';
import Filters from 'pages/MyScorecards/Filters';
import {
  CreateScorecardSlot,
  FiltersGroup,
  SwitcherContainer,
  StyledSwitchMenu,
  SwitcherText,
} from './Home.styled';

type TabType = 'scorecards' | 'pdp';

const HOME_ACTIVE_TAB_STORAGE_KEY = 'v_home.selectedTab';

export default function Home() {
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isCreateDialogOpen, setCreateDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const storedTab = localStorage.getItem(
      HOME_ACTIVE_TAB_STORAGE_KEY,
    ) as TabType | null;
    if (storedTab === 'scorecards' || storedTab === 'pdp') {
      return storedTab;
    }
    if (location.pathname.includes('personal-development-plan')) {
      return 'pdp';
    }
    return 'scorecards';
  });

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    localStorage.setItem(HOME_ACTIVE_TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  useEffect(() => {
    const isOnScorecardsRoute = location.pathname.includes('my-scorecards');
    const isOnPdpRoute = location.pathname.includes(
      'personal-development-plan',
    );

    if (isOnScorecardsRoute || isOnPdpRoute || location.pathname === '/home') {
      if (activeTab === 'pdp' && !isOnPdpRoute) {
        navigate(`/${URL_PERSONAL_DEVELOPMENT_PLAN}`, { replace: true });
      } else if (activeTab === 'scorecards' && !isOnScorecardsRoute) {
        navigate(`/${URL_MY_SCORECARDS}`, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    if (tab === 'scorecards') {
      navigate(`/${URL_MY_SCORECARDS}`);
    } else {
      navigate(`/${URL_PERSONAL_DEVELOPMENT_PLAN}`);
    }
    handleClose();
  };

  const isToShowHeader = [
    '/home/personal-development-plan/list',
    '/home/my-scorecards/list',
  ].includes(location.pathname);

  if (!isToShowHeader) {
    return (
      <PageContainer hasAccess visibleForPathname={location.pathname}>
        <Outlet />
      </PageContainer>
    );
  }

  return (
    <PageContainer hasAccess visibleForPathname={location.pathname}>
      <PageHeader>
        <PageTitleWrapper>
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <PageTitle sx={{ marginRight: 0 }}>
              <FormattedMessage {...commonTexts.home} />
              <SwitcherContainer>
                <SwitcherText onClick={handleClick}>
                  {activeTab === 'scorecards' ? (
                    <FormattedMessage {...scorecardsTexts.title} />
                  ) : (
                    <FormattedMessage {...pdpTexts.developmentPlans} />
                  )}
                  <KeyboardArrowDown
                    sx={{
                      transform: open ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.2s',
                    }}
                  />
                </SwitcherText>
                <StyledSwitchMenu
                  onClose={handleClose}
                  anchorEl={anchorEl}
                  open={open}
                >
                  <MenuItem
                    selected={activeTab === 'scorecards'}
                    onClick={() => handleTabChange('scorecards')}
                  >
                    <FormattedMessage {...scorecardsTexts.title} />
                  </MenuItem>
                  <MenuItem
                    selected={activeTab === 'pdp'}
                    onClick={() => handleTabChange('pdp')}
                  >
                    <FormattedMessage {...pdpTexts.developmentPlans} />
                  </MenuItem>
                </StyledSwitchMenu>
              </SwitcherContainer>
            </PageTitle>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              minWidth: 0,
              px: 3,
            }}
          >
            {/* Temporarily hidden. Restore when Review Cycle Timeline work resumes:
            {activeTab === 'scorecards' && (
              <ReviewCycleTimeline
                audience="individual"
                selectedCycleId={reviewCycleIds}
              />
            )}
            */}
          </Box>
          <FiltersGroup>
            {/* Temporarily hidden. Restore when Performance Review Flow should be visible again:
            <PerfReviewFlowSlot>
              <PerformanceReviewFlow />
            </PerfReviewFlowSlot>
            */}
            {activeTab === 'scorecards' && <Filters />}
            {activeTab === 'scorecards' && (
              <CreateScorecardSlot>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => setCreateDialogOpen(true)}
                >
                  <FormattedMessage {...createScorecardDialogMessages.button} />
                </Button>
              </CreateScorecardSlot>
            )}
          </FiltersGroup>
        </PageTitleWrapper>
      </PageHeader>
      <Outlet />
      {isCreateDialogOpen && (
        <CreateScorecardDialog onClose={() => setCreateDialogOpen(false)} />
      )}
    </PageContainer>
  );
}
